/**
 * Receipt numbers.
 *
 * Each till has its own series so that two tills selling with no internet can
 * never produce the same number: T1-000123 and T2-000045. Within one series the
 * numbers have no gaps, which is what a tax inspector wants to see.
 *
 * The number is allocated on the DEVICE, from a counter kept in the device's own
 * storage, so it works with the connection down. The server refuses a second sale
 * with the same (device, counter) pair, which stops a retry creating a duplicate.
 */

export interface ReceiptSeries {
  /** Short till name, e.g. "T1". */
  prefix: string;
  /** The last number used. The next sale gets last + 1. */
  last: number;
  digits: number;
}

export function formatReceiptNo(prefix: string, counter: number, digits = 6): string {
  return `${prefix.toUpperCase()}-${String(counter).padStart(digits, '0')}`;
}

export function parseReceiptNo(receiptNo: string): { prefix: string; counter: number } | null {
  const m = /^([A-Za-z]{1,4}\d?)-(\d{1,10})$/.exec(receiptNo.trim());
  if (!m) return null;
  return { prefix: m[1].toUpperCase(), counter: Number(m[2]) };
}

export function nextReceipt(series: ReceiptSeries): { receiptNo: string; series: ReceiptSeries } {
  const next = series.last + 1;
  return {
    receiptNo: formatReceiptNo(series.prefix, next, series.digits),
    series: { ...series, last: next },
  };
}

/** Find a hole in a series — run nightly so a missing receipt is noticed at once. */
export function findGaps(counters: number[]): number[] {
  if (counters.length === 0) return [];
  const sorted = [...new Set(counters)].sort((a, b) => a - b);
  const gaps: number[] = [];
  for (let i = sorted[0]; i <= sorted[sorted.length - 1]; i++) {
    if (!sorted.includes(i)) gaps.push(i);
  }
  return gaps;
}
